import {
  StemPlayer,
  loadMasterStretch,
  type LoadedStem,
  type MasterStretchAdapter,
  type PlayerSnapshot,
  type StemMixSettings,
  type StemRole,
} from "@repo/audio-engine";
import type { BenchPayload } from "../lib/oscilloscopeUtils";
import {
  loadBackendSongByReference,
  loadBackendSongByYoutubeId,
  loadRandomBackendSong,
  resolveRandomBackendSongDescriptor,
  type BackendSongReference,
  type RandomBackendSongDescriptor,
  type StemLoadProgress,
} from "./backendSongLoader";

export interface OutputTimeCapture {
  readonly nowWallClockMs: number;
  readonly nowPerfTimeMs: number;
  readonly executePerfTimeMs: number;
  readonly outputPerfTimeMs: number;
  readonly outputContextTimeSec: number;
  readonly projectedContextAtExecuteSec: number;
}

export class LabPlaybackController {
  private readonly ctx: AudioContext;
  private player: StemPlayer | null = null;
  private stretch: MasterStretchAdapter | null = null;

  constructor(ctx: AudioContext) {
    this.ctx = ctx;
  }

  async initialize(): Promise<void> {
    const diagnostics: string[] = [];
    if (!globalThis.isSecureContext) {
      diagnostics.push("isSecureContext=false");
    }
    if (!globalThis.crossOriginIsolated) {
      diagnostics.push("crossOriginIsolated=false");
    }
    const maybeAudioWorklet = (this.ctx as unknown as { audioWorklet?: { addModule?: unknown } })
      .audioWorklet;
    if (typeof maybeAudioWorklet?.addModule !== "function") {
      diagnostics.push("AudioContext.audioWorklet unavailable");
    }
    if (diagnostics.length > 0) {
      throw new Error(
        `Signalsmith prerequisites missing: ${diagnostics.join(", ")}. ` +
          "Run in a secure, cross-origin-isolated context with AudioWorklet support.",
      );
    }
    try {
      this.stretch = await loadMasterStretch(this.ctx);
    } catch (error) {
      this.stretch = null;
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(
        `Signalsmith stretch failed to initialize. Run in a cross-origin-isolated secure context and ensure worklets are enabled. Root error: ${message}`,
      );
    }
    this.player = new StemPlayer({
      ctx: this.ctx,
      masterStretch: this.stretch,
    });
  }

  setLoadedStems(stems: readonly LoadedStem[]): void {
    this.requirePlayer().setStems(stems);
  }

  getStemRoles(): readonly StemRole[] {
    return this.requirePlayer().getStemRoles();
  }

  getStemSettings(role: StemRole): StemMixSettings | undefined {
    return this.requirePlayer().getStemSettings(role);
  }

  setStemSettings(role: StemRole, settings: Partial<StemMixSettings>): void {
    this.requirePlayer().setStemSettings(role, settings);
  }

  getStemLevel(role: StemRole): number {
    return this.requirePlayer().getStemLevel(role);
  }

  async loadRandomSongFromBackend(
    apiBaseUrl: string,
    onStemProgress?: (progress: StemLoadProgress) => void,
    signal?: AbortSignal,
  ): Promise<{
    youtubeId: string;
    title: string;
    stemCount: number;
    smamsUrl?: string;
    stemUrlsByRole: Readonly<Partial<Record<StemRole, string>>>;
  }> {
    const loadedSong = await loadRandomBackendSong(this.ctx, apiBaseUrl, {
      ...(onStemProgress !== undefined ? { onStemProgress } : {}),
      ...(signal !== undefined ? { signal } : {}),
    });
    this.requirePlayer().setStems(loadedSong.stems);
    return {
      youtubeId: loadedSong.youtubeId,
      title: loadedSong.title,
      stemCount: loadedSong.stems.length,
      stemUrlsByRole: loadedSong.stemUrlsByRole,
      ...(loadedSong.smamsUrl ? { smamsUrl: loadedSong.smamsUrl } : {}),
    };
  }

  async loadRandomSongDescriptorFromBackend(
    apiBaseUrl: string,
    signal?: AbortSignal,
  ): Promise<RandomBackendSongDescriptor> {
    return resolveRandomBackendSongDescriptor(apiBaseUrl, signal);
  }

  async loadSongFromBackendByYoutubeId(
    apiBaseUrl: string,
    youtubeId: string,
    onStemProgress?: (progress: StemLoadProgress) => void,
    signal?: AbortSignal,
  ): Promise<{
    youtubeId: string;
    title: string;
    stemCount: number;
    smamsUrl?: string;
    stemUrlsByRole: Readonly<Partial<Record<StemRole, string>>>;
  }> {
    const loadedSong = await loadBackendSongByYoutubeId(this.ctx, apiBaseUrl, youtubeId, {
      ...(onStemProgress !== undefined ? { onStemProgress } : {}),
      ...(signal !== undefined ? { signal } : {}),
    });
    this.requirePlayer().setStems(loadedSong.stems);
    return {
      youtubeId: loadedSong.youtubeId,
      title: loadedSong.title,
      stemCount: loadedSong.stems.length,
      stemUrlsByRole: loadedSong.stemUrlsByRole,
      ...(loadedSong.smamsUrl ? { smamsUrl: loadedSong.smamsUrl } : {}),
    };
  }

  async loadSongFromBackendReference(
    apiBaseUrl: string,
    reference: BackendSongReference,
    onStemProgress?: (progress: StemLoadProgress) => void,
    signal?: AbortSignal,
  ): Promise<{
    youtubeId: string;
    title: string;
    stemCount: number;
    smamsUrl?: string;
    stemUrlsByRole: Readonly<Partial<Record<StemRole, string>>>;
  }> {
    const loadedSong = await loadBackendSongByReference(this.ctx, apiBaseUrl, reference, {
      ...(onStemProgress !== undefined ? { onStemProgress } : {}),
      ...(signal !== undefined ? { signal } : {}),
    });
    this.requirePlayer().setStems(loadedSong.stems);
    return {
      youtubeId: loadedSong.youtubeId,
      title: loadedSong.title,
      stemCount: loadedSong.stems.length,
      stemUrlsByRole: loadedSong.stemUrlsByRole,
      ...(loadedSong.smamsUrl ? { smamsUrl: loadedSong.smamsUrl } : {}),
    };
  }

  async resumeContext(): Promise<void> {
    if (this.ctx.state !== "running") {
      await this.ctx.resume();
    }
  }

  snapshot(): PlayerSnapshot {
    return this.requirePlayer().snapshot();
  }

  captureOutputTimeModel(executePerfTimeMs: number): OutputTimeCapture {
    const nowWallClockMs = Date.now();
    const nowPerfTimeMs = performance.now();
    const outputTimestamp = this.ctx.getOutputTimestamp();
    const outputPerfTimeMs = outputTimestamp.performanceTime ?? nowPerfTimeMs;
    const outputContextTimeSec = outputTimestamp.contextTime ?? this.ctx.currentTime;
    const deltaMs = Math.max(0, executePerfTimeMs - nowPerfTimeMs);
    const projectedContextAtExecuteSec = outputContextTimeSec + deltaMs / 1000;
    return {
      nowWallClockMs,
      nowPerfTimeMs,
      executePerfTimeMs,
      outputPerfTimeMs,
      outputContextTimeSec,
      projectedContextAtExecuteSec,
    };
  }

  estimateAudibleOutputLatencyMs(): number {
    const maybeOutputLatency = (this.ctx as unknown as { outputLatency?: number }).outputLatency;
    const outputLatencySec =
      typeof maybeOutputLatency === "number" && Number.isFinite(maybeOutputLatency)
        ? maybeOutputLatency
        : 0;
    const baseLatencySec =
      typeof this.ctx.baseLatency === "number" && Number.isFinite(this.ctx.baseLatency)
        ? this.ctx.baseLatency
        : 0;
    // Clamp to a sensible range so broken browser values cannot destabilize scheduling.
    const combinedMs = (outputLatencySec + baseLatencySec) * 1000;
    return Math.max(0, Math.min(180, combinedMs));
  }

  applyCommand(payload: BenchPayload): void {
    const player = this.requirePlayer();
    switch (payload.op) {
      case "transport.play":
        player.play();
        return;
      case "transport.pause":
        player.pause();
        return;
      case "transport.seek":
        {
          const seekPositionSec = this.resolveSeekPositionSec(payload);
          if (seekPositionSec !== null) {
            player.seek(seekPositionSec);
          }
        }
        return;
      case "transport.setRate":
        if (payload.rate !== undefined) {
          player.setPlaybackRate(payload.rate);
        }
        return;
      case "transport.setPitch":
        if (payload.pitchSemitones !== undefined) {
          player.setPitchSemitones(payload.pitchSemitones);
        }
        return;
      case "transport.setSong":
        // Song loading is handled asynchronously by App scheduleAudioApply.
        return;
      case "transport.setLead":
        // Scheduled lead is a session/UI sync variable, not an audio engine command.
        return;
      case "transport.setLatencyComp":
        // Latency compensation toggle is scheduler/UI behavior, not an audio engine command.
        return;
      default:
        return;
    }
  }

  private resolveSeekPositionSec(payload: BenchPayload): number | null {
    if (payload.toPositionSec !== undefined) {
      return payload.toPositionSec;
    }
    const asRecord = payload as unknown as Record<string, unknown>;
    const fallbackSeconds = asRecord.targetPositionSec;
    if (typeof fallbackSeconds === "number" && Number.isFinite(fallbackSeconds)) {
      return fallbackSeconds;
    }
    const fallbackMs = asRecord.toPositionMs ?? asRecord.targetPositionMs;
    if (typeof fallbackMs === "number" && Number.isFinite(fallbackMs)) {
      return fallbackMs / 1000;
    }
    return null;
  }

  dispose(): void {
    this.player?.dispose();
    this.player = null;
    this.stretch = null;
    void this.ctx.close();
  }

  private requirePlayer(): StemPlayer {
    if (!this.player) {
      throw new Error("LabPlaybackController is not initialized");
    }
    return this.player;
  }
}
